import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance, { endpoints } from 'src/utils/axios';
import { requestNotificationPermission, registerFcmTokenWithBackend, unregisterFcmToken } from 'src/utils/firebase';

// ----------------------------------------------------------------------

// Async thunk for login
export const login = createAsyncThunk('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(endpoints.auth.login, credentials);

    if (response.data.success && response.data.data?.token) {
      const { token, user } = response.data.data;

      localStorage.setItem('accessToken', token);
      localStorage.setItem('user', JSON.stringify(user));

      if (user.requirePasswordChange) {
        localStorage.setItem('requirePasswordChange', 'true');
      } else {
        localStorage.removeItem('requirePasswordChange');
      }

      // Fetch user rights immediately after login (Now that token is in localStorage)
      let rights = null;
      try {
        const userId = user.id || user._id;
        const rightsResponse = await axiosInstance.get(endpoints.specificUserRights.get(userId));
        if (rightsResponse.data?.success) {
          const { rights: userRights } = rightsResponse.data.data;
          rights = userRights;
        }
      } catch (err) {
        console.error('Failed to preload user rights:', err);
      }

      if (rights) {
        localStorage.setItem('userRights', JSON.stringify(rights));
      }

      return { accessToken: token, user, rights };
    }

    return rejectWithValue('Invalid response from server');
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Login failed';
    return rejectWithValue(errorMessage);
  }
});

// Async thunk for register
export const register = createAsyncThunk('auth/register', async (userData, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(endpoints.auth.register, userData);
    if (response.data.accessToken) {
      localStorage.setItem('accessToken', response.data.accessToken);
    }
    return response.data;
  } catch (error) {
    return rejectWithValue(error.message || 'Registration failed');
  }
});

// Async thunk for getting current user
export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(endpoints.auth.me);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to get user');
    }
  }
);

// Async thunk for logout
export const logout = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
  try {
    // Unregister FCM token before clearing localStorage (token is read from localStorage inside)
    await unregisterFcmToken();
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userRights');
    return null;
  } catch (error) {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userRights');
    return rejectWithValue(error.message || 'Logout failed');
  }
});

// Async thunk for refreshing user data
export const refreshUser = createAsyncThunk('auth/refreshUser', async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(endpoints.auth.me);
    if (response.data.success && response.data.data) {
      const userData = response.data.data;
      localStorage.setItem('user', JSON.stringify(userData));
      return userData;
    }
    return rejectWithValue('Invalid response from server');
  } catch (error) {
    return rejectWithValue(error.message || 'Failed to refresh user data');
  }
});

// Async thunk for fetching user rights
export const fetchUserRights = createAsyncThunk('auth/fetchRights', async (userId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(endpoints.specificUserRights.get(userId));
    if (response.data?.success) {
      const { rights } = response.data.data;
      localStorage.setItem('userRights', JSON.stringify(rights));
      return rights;
    }
    return rejectWithValue('Failed to fetch rights');
  } catch (error) {
    return rejectWithValue(error.message || 'Failed to fetch user rights');
  }
});

// ----------------------------------------------------------------------

const getInitialState = () => {
  const accessToken = localStorage.getItem('accessToken');
  const userStr = localStorage.getItem('user');
  const rightsStr = localStorage.getItem('userRights');

  return {
    user: userStr ? JSON.parse(userStr) : null,
    rights: rightsStr ? JSON.parse(rightsStr) : null,
    accessToken: accessToken || null,
    isAuthenticated: !!accessToken,
    isLoading: false,
    error: null,
  };
};

// ----------------------------------------------------------------------

const authSlice = createSlice({
  name: 'auth',
  initialState: getInitialState(),
  reducers: {
    clearAuth: (state) => {
      state.user = null;
      state.rights = null;
      state.accessToken = null;
      state.isAuthenticated = false;
      state.error = null;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      localStorage.removeItem('userRights');
    },
    setRights: (state, action) => {
      state.rights = action.payload;
      localStorage.setItem('userRights', JSON.stringify(action.payload));
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.rights = action.payload.rights;
        state.accessToken = action.payload.accessToken;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.payload;
      })
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.accessToken = action.payload.accessToken;
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state) => {
        state.isLoading = false;
        state.isAuthenticated = true;
      })
      .addCase(getCurrentUser.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.accessToken = null;
      })
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.accessToken = null;
        state.error = null;
      })
      .addCase(logout.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.accessToken = null;
      })
      .addCase(refreshUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.error = null;
      })
      .addCase(fetchUserRights.fulfilled, (state, action) => {
        state.rights = action.payload;
      });
  },
});

export const { clearAuth, clearError, setRights } = authSlice.actions;
export default authSlice.reducer;
